import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import { const_var, params } from "../../../../redux/reducers/reducer";
import { extrudeSettings, railModel, roofBaseRailModel } from "../../../../utils/Functions";
import { CSG } from "../../../../assets/libs/csg-lib";
import { MaterialTruss } from "../../CustomMaterial";

const BaseRailModel = ({ normalMap, envMap, meshDelModel_CSG }) => {
	const obj_baseRail = new THREE.Object3D();
	const obj_singleSlopeBaseRail = new THREE.Object3D();

	const [baseRail, setBaseRail] = useState(new THREE.InstancedBufferAttribute(new Float32Array([]), 16));
	const ref_singleSlopeBaseRail = useRef();

	const railWidth = 0.2;
	const railHeight = 0.2;
	const offset = 0.03;

	const roofAlpha = Math.atan(params.p_r_p / 12);
	const dynamicHeight = params.singleSlope && params.freeStandingSide === "left" ? params.p_h - (params.p_r_p / 12) * params.p_w : params.p_h;
	const dynamicHeightRight = params.singleSlope ? params.p_h - (params.p_r_p / 12) * railWidth : params.p_h;
	const outInDistance = useMemo(() => {
		return const_var.legstype === "double" ? 0.21 : const_var.legstype === "ladder" ? 0.5 : const_var.legstype === "zigzag" ? 1 : 0;
	}, [const_var.legstype]);
	const singleSlopeCount = Math.floor(params.p_w / 3.5);
	const singleSlopeDistance = (params.p_w - (singleSlopeCount - 1) * 3.5) / 2;

	const unperOffset = useMemo(() => {
		if (params.p_b_t === 27) return -0.19 + (params.p_r_p - 2) * (-0.1 / 4);
		else return -0.1;
	}, [params.p_b_t, params.p_r_p]);

	const roofBaseRailArr = useMemo(() => {
		let dataInfoArray = [];
		let count, distance;

		const initRoofSpace = 0.5;
		let initDistance = 3.5;
		const tempCount = Math.floor((params.p_w / 2 - initRoofSpace) / initDistance);
		if (params.p_w / 2 - initRoofSpace === tempCount * initDistance) {
			count = tempCount;
			distance = initDistance;
		} else if (params.p_w / 2 - initRoofSpace - tempCount * initDistance <= initDistance / 2) {
			count = tempCount;
			distance = (params.p_w / 2 - initRoofSpace) / count;
		} else {
			count = tempCount + 1;
			distance = (params.p_w / 2 - initRoofSpace) / count;
		}

		for (let i = 0; i < count; i++) {
			const pos_x = initRoofSpace + distance * i - railWidth / 2;
			const pos_y = params.p_h + ((params.p_w / 2 - pos_x) * params.p_r_p) / 12 - offset;

			const tempInfoObj = {
				distance: distance,
				pos_x: pos_x,
				pos_y: pos_y,
				length: params.p_d
			};

			dataInfoArray.push(tempInfoObj);
		}

		return dataInfoArray;
	}, [params.p_h, params.p_d, params.p_w, params.p_r_p]);

	const baseRailLength = useMemo(() => {
		let baseRailLength;
		roofBaseRailArr.map((item_roof_rail) => {
			baseRailLength = item_roof_rail.length;
		});
		return baseRailLength;
	}, [roofBaseRailArr]);

	const CSGSideBottomRailData = useMemo(() => {
		const modelShape = railModel(railWidth, railHeight);

		const meshBottomRailModel = new THREE.Mesh(new THREE.ExtrudeGeometry(modelShape, extrudeSettings(params.p_d)), new THREE.MeshStandardMaterial({ color: "pink" }));
		const meshStorageBottomRailModel = new THREE.Mesh(new THREE.ExtrudeGeometry(modelShape, extrudeSettings(params.p_d + 0.3)), new THREE.MeshStandardMaterial({ color: "pink" }));

		let meshLeftBottomRailModel_Temp = meshBottomRailModel.clone();
		meshLeftBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2);
		meshLeftBottomRailModel_Temp.updateMatrix();
		meshLeftBottomRailModel_Temp.updateMatrixWorld();
		const meshLeftBottomRailModel_CSG = CSG.fromMesh(meshLeftBottomRailModel_Temp);

		let meshLeftStorageBottomRailModel_Temp = meshStorageBottomRailModel.clone();
		meshLeftStorageBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2 - 0.15);
		meshLeftStorageBottomRailModel_Temp.updateMatrix();
		meshLeftStorageBottomRailModel_Temp.updateMatrixWorld();
		const meshLeftStorageBottomRailModel_CSG = CSG.fromMesh(meshLeftStorageBottomRailModel_Temp);

		let meshRightBottomRailModel_Temp = meshBottomRailModel.clone();
		meshRightBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2);
		meshRightBottomRailModel_Temp.updateMatrix();
		meshRightBottomRailModel_Temp.updateMatrixWorld();
		const meshRightBottomRailModel_CSG = CSG.fromMesh(meshRightBottomRailModel_Temp);

		let meshRightStorageBottomRailModel_Temp = meshStorageBottomRailModel.clone();
		meshRightStorageBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2 - 0.15);
		meshRightStorageBottomRailModel_Temp.updateMatrix();
		meshRightStorageBottomRailModel_Temp.updateMatrixWorld();
		const meshRightStorageBottomRailModel_CSG = CSG.fromMesh(meshRightStorageBottomRailModel_Temp);

		let meshFrontOffsetWithDoorDelModel = CSG.toMesh(meshDelModel_CSG.front, new THREE.Matrix4());
		meshFrontOffsetWithDoorDelModel.position.set(0, 0, params.p_d / 2 + 1.45);
		meshFrontOffsetWithDoorDelModel.updateMatrixWorld();

		let meshBackOffsetWithDoorDelModel = CSG.toMesh(meshDelModel_CSG.back, new THREE.Matrix4());
		meshBackOffsetWithDoorDelModel.position.set(0, 0, -(params.p_d / 2 + 1.45));
		meshBackOffsetWithDoorDelModel.updateMatrixWorld();

		const meshFrontOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshFrontOffsetWithDoorDelModel);
		const meshBackOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshBackOffsetWithDoorDelModel);

		const leftBottomRailModel_CSG = meshLeftBottomRailModel_CSG.subtract(meshDelModel_CSG.left);
		const leftStorageBottomRail_CSG = meshLeftStorageBottomRailModel_CSG.subtract(meshDelModel_CSG.leftStorageFront);
		const leftStorageBottomRailModel_CSG = leftStorageBottomRail_CSG.subtract(meshFrontOffsetWithDoorDelModel_CSG);
		const leftStorageOffsetWithDoorBottomRailModel_CSG = leftStorageBottomRailModel_CSG.subtract(meshBackOffsetWithDoorDelModel_CSG);

		const rightBottomRailModel_CSG = meshRightBottomRailModel_CSG.subtract(meshDelModel_CSG.right);
		const rightStorageBottomRail_CSG = meshRightStorageBottomRailModel_CSG.subtract(meshDelModel_CSG.rightStorageFront);
		const rightStorageBottomRailModel_CSG = rightStorageBottomRail_CSG.subtract(meshFrontOffsetWithDoorDelModel_CSG);
		const rightStorageOffsetWithDoorBottomRailModel_CSG = rightStorageBottomRailModel_CSG.subtract(meshBackOffsetWithDoorDelModel_CSG);

		const CSGLeftBottomRailModel = CSG.toMesh(leftBottomRailModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: "#A4A7AA" }));

		const CSGLeftStorageBottomRailModel = CSG.toMesh(leftStorageOffsetWithDoorBottomRailModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: "#A4A7AA" }));

		const CSGRightBottomRailModel = CSG.toMesh(rightBottomRailModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: "#A4A7AA" }));

		const CSGRightStorageBottomRailModel = CSG.toMesh(rightStorageOffsetWithDoorBottomRailModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: "#A4A7AA" }));

		return { CSGLeftBottomRailModel, CSGRightBottomRailModel, CSGLeftStorageBottomRailModel, CSGRightStorageBottomRailModel };
	}, [params.p_d, meshDelModel_CSG.left, meshDelModel_CSG.right, meshDelModel_CSG.leftStorageFront, meshDelModel_CSG.rightStorageFront]);

	const updateInstancedBaseRailMesh = useCallback(() => {
		let index_all = 0;
		const instancedMatBaseRail = [];
		new Array(2).fill("").forEach((_, index_dir) => {
			let offsetForDir = 1;
			if (index_dir === 1) offsetForDir = 1;
			else offsetForDir = -1;

			roofBaseRailArr.forEach((item_roof_rail, index_truss) => {
				obj_baseRail.position.set(offsetForDir * item_roof_rail.pos_x, item_roof_rail.pos_y, -params.p_d / 2);
				obj_baseRail.rotation.z = offsetForDir * -roofAlpha;

				if (params.p_r_s === 3 && !params.singleSlope) obj_baseRail.scale.setScalar(1);
				else obj_baseRail.scale.setScalar(0);

				obj_baseRail.updateMatrix();
				instancedMatBaseRail.push(...obj_baseRail.matrix.elements);

				index_all++;
			});
		});

		setBaseRail(new THREE.InstancedBufferAttribute(new Float32Array(instancedMatBaseRail), 16));
	}, [roofBaseRailArr, roofAlpha, params.p_d, params.p_r_s, params.singleSlope]);

	useLayoutEffect(() => {
		updateInstancedBaseRailMesh();
	}, [updateInstancedBaseRailMesh]);

	const updateInstancedSingleSlopeBaseRailMesh = useCallback(() => {
		if (ref_singleSlopeBaseRail.current) {
			let index_all = 0;

			new Array(singleSlopeCount).fill("").forEach((_, index_roof_rail) => {
				obj_singleSlopeBaseRail.position.set(
					-(params.p_w / 2) + singleSlopeDistance + index_roof_rail * 3.5,
					params.p_h - (params.p_r_p / 12) * params.p_w + (params.p_r_p / 12) * (3.5 * index_roof_rail + singleSlopeDistance) - offset,
					-params.p_d / 2
				);
				obj_singleSlopeBaseRail.rotation.z = roofAlpha;

				if (params.freeStandingSide == "left" && params.singleSlope) obj_singleSlopeBaseRail.scale.setScalar(1);
				else obj_singleSlopeBaseRail.scale.setScalar(0);

				obj_singleSlopeBaseRail.updateMatrix();
				ref_singleSlopeBaseRail.current.setMatrixAt(index_all, obj_singleSlopeBaseRail.matrix);

				index_all++;
			});
			ref_singleSlopeBaseRail.current.instanceMatrix.needsUpdate = true;
		}
	}, [ref_singleSlopeBaseRail.current, singleSlopeCount, roofAlpha, params.p_d, params.freeStandingSide, params.singleSlope, params.p_r_p, singleSlopeDistance, params.p_w, params.p_h]);

	useLayoutEffect(() => {
		updateInstancedSingleSlopeBaseRailMesh();
	}, [updateInstancedSingleSlopeBaseRailMesh]);

	return (
		<group name="base-rail-legs">
			<mesh name="left_leg_base_rail" geometry={CSGSideBottomRailData.CSGLeftBottomRailModel.geometry} position={[-(params.p_w / 2 - railHeight / 2) + 0.055, 0, 0]}>
				<MaterialTruss normalMap={normalMap} envMap={envMap} />
			</mesh>
			<mesh name="right_leg_base_rail" geometry={CSGSideBottomRailData.CSGRightBottomRailModel.geometry} position={[params.p_w / 2 - railHeight / 2 - 0.055, 0, 0]}>
				<MaterialTruss normalMap={normalMap} envMap={envMap} />
			</mesh>

			{params.p_e_l == true && (const_var.legstype == "double" || const_var.legstype == "ladder" || const_var.legstype == "zigzag") ? (
				<>
					<mesh name="left_leg_base_rail_double" geometry={CSGSideBottomRailData.CSGLeftBottomRailModel.geometry} position={[-(params.p_w / 2 - railHeight / 2) + outInDistance, 0, 0]}>
						<MaterialTruss normalMap={normalMap} envMap={envMap} />
					</mesh>
					<mesh name="right_leg_base_rail_double" geometry={CSGSideBottomRailData.CSGRightBottomRailModel.geometry} position={[params.p_w / 2 - railHeight / 2 - outInDistance, 0, 0]}>
						<MaterialTruss normalMap={normalMap} envMap={envMap} />
					</mesh>
				</>
			) : null}

			{new Array(2).fill("").map((_, index_invert) => (
				<group name="base-rail-model-legs" key={`base-rail-model-${index_invert}`}>
					{params.p_r_s === 3 && (
						<>
							<mesh
								name="unper"
								position={[
									Math.pow(-1, index_invert) * (params.p_w / 2 - railHeight / 1.3),
									params.singleSlope && params.freeStandingSide == "left" && index_invert == 1
										? dynamicHeight - 0.12
										: params.singleSlope && index_invert == 0
											? dynamicHeightRight - 0.12
											: params.p_h + unperOffset,
									-params.p_d / 2
								]}
								rotation={[0, 0, params.singleSlope && params.freeStandingSide == "left" ? 0 : 0]}
							>
								<extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(params.p_d)]} />
								<MaterialTruss normalMap={normalMap} envMap={envMap} />
							</mesh>
						</>
					)}
				</group>
			))}

			{baseRail.array.length > 0 &&
				[1].map((item) => (
					<instancedMesh key={baseRail.array.length} instanceMatrix-needsUpdate={true} instanceMatrix={baseRail} args={[null, null, roofBaseRailArr.length * 2]} frustumCulled={false}>
						<extrudeGeometry args={[roofBaseRailModel(), extrudeSettings(baseRailLength)]} />
						<MaterialTruss normalMap={normalMap} envMap={envMap} />
					</instancedMesh>
				))}

			<instancedMesh ref={ref_singleSlopeBaseRail} args={[null, null, singleSlopeCount]} frustumCulled={false}>
				<extrudeGeometry args={[roofBaseRailModel(), extrudeSettings(params.p_d)]} />
				<MaterialTruss normalMap={normalMap} envMap={envMap} />
			</instancedMesh>

			{params.cB_addStorage_check_right && (
				<mesh name="rightStorage" geometry={CSGSideBottomRailData.CSGRightStorageBottomRailModel.geometry} position={[params.p_w / 2 - parseInt(params.cB_addStorage_right) + railWidth / 2 + 0.05, 0, 0]}>
					<MaterialTruss normalMap={normalMap} envMap={envMap} />
				</mesh>
			)}
			{params.cB_addStorage_check_left && (
				<mesh name="leftStorage" geometry={CSGSideBottomRailData.CSGLeftStorageBottomRailModel.geometry} position={[-(params.p_w / 2 - parseInt(params.cB_addStorage_left) + railWidth / 2 + 0.05), 0, 0]}>
					<MaterialTruss normalMap={normalMap} envMap={envMap} />
				</mesh>
			)}
		</group>
	);
};

export default BaseRailModel;
